import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Shield, 
  CreditCard, 
  Lock, 
  CheckCircle, 
  AlertCircle,
  Download,
  Share,
  Leaf,
  Calculator,
  Info
} from 'lucide-react';

const PurchaseCredits = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [quantity, setQuantity] = useState('');
  const [retireCredits, setRetireCredits] = useState(true);
  const [retirementReason, setRetirementReason] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentDetails, setPaymentDetails] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    name: '',
    email: '',
    company: '',
    address: '',
    city: '',
    country: '',
    zipCode: ''
  });
  const [processing, setProcessing] = useState(false);
  const [purchaseComplete, setPurchaseComplete] = useState(false);
  const [certificateId, setCertificateId] = useState('');
  const [errors, setErrors] = useState({});

  // Mock project data
  const mockProject = {
    id: parseInt(id),
    title: "Amazon Rainforest Conservation",
    projectType: "Forest Conservation",
    price: 25,
    availableCredits: 35000,
    minimumPurchase: 10,
    verificationStatus: "Gold Standard",
    developer: "EcoForest Initiative"
  };

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setProject(mockProject);
      setLoading(false);
    }, 1000);
  }, [id]);

  const validateStep = (step) => {
    const stepErrors = {};

    switch (step) {
      case 1:
        if (!quantity) {
          stepErrors.quantity = 'Quantity is required';
        } else if (parseInt(quantity) < project.minimumPurchase) {
          stepErrors.quantity = `Minimum purchase is ${project.minimumPurchase} credits`;
        } else if (parseInt(quantity) > project.availableCredits) {
          stepErrors.quantity = 'Quantity exceeds available credits';
        }
        if (retireCredits && !retirementReason) {
          stepErrors.retirementReason = 'Retirement reason is required';
        }
        break;

      case 2:
        if (!paymentMethod) {
          stepErrors.paymentMethod = 'Payment method is required';
        }
        if (!paymentDetails.name) {
          stepErrors.name = 'Name is required';
        }
        if (!paymentDetails.email) {
          stepErrors.email = 'Email is required';
        }
        if (paymentMethod === 'credit-card') {
          if (!paymentDetails.cardNumber) {
            stepErrors.cardNumber = 'Card number is required';
          }
          if (!paymentDetails.expiryDate) {
            stepErrors.expiryDate = 'Expiry date is required';
          }
          if (!paymentDetails.cvv) {
            stepErrors.cvv = 'CVV is required';
          }
        }
        break;
    }

    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handlePurchase = async () => {
    if (!validateStep(2)) return;

    setProcessing(true);
    
    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Generate certificate ID
      const certId = `CC-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
      setCertificateId(certId);
      setPurchaseComplete(true);
      setCurrentStep(3);
    } catch (error) {
      console.error('Payment error:', error);
      alert('Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const calculateTotal = () => {
    if (!quantity || !project) return 0;
    return parseInt(quantity) * project.price;
  };

  const calculateFees = () => {
    const subtotal = calculateTotal();
    return subtotal * 0.03; // 3% processing fee
  };

  const downloadCertificate = () => {
    // Simulate certificate download
    const certificateData = {
      certificateId,
      projectTitle: project.title,
      quantity: parseInt(quantity),
      totalCost: calculateTotal() + calculateFees(),
      purchaseDate: new Date().toISOString(),
      retirementDetails: retireCredits ? {
        retired: true,
        reason: retirementReason,
        retirementDate: new Date().toISOString()
      } : null
    };
    
    console.log('Certificate data:', certificateData);
    alert('Certificate download started!');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Project Not Found</h2>
        <Link to="/marketplace" className="text-green-600 hover:text-green-700">
          Return to Marketplace
        </Link>
      </div>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Select Credits</h3>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h4 className="font-medium text-gray-900 mb-2">{project.title}</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Price per credit:</span>
                    <span className="font-medium ml-2">${project.price}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Available:</span>
                    <span className="font-medium ml-2">{project.availableCredits.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Minimum purchase:</span>
                    <span className="font-medium ml-2">{project.minimumPurchase} credits</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Verification:</span>
                    <span className="font-medium ml-2 flex items-center">
                      <Shield className="h-4 w-4 text-green-600 mr-1" />
                      {project.verificationStatus}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Credits *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={project.minimumPurchase}
                      max={project.availableCredits}
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                        errors.quantity ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={`Min ${project.minimumPurchase} credits`}
                    />
                    <Calculator className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  </div>
                  {errors.quantity && <p className="text-red-500 text-xs mt-1">{errors.quantity}</p>}
                </div>

                {quantity && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Environmental Impact</h4>
                    <div className="flex items-center text-green-700">
                      <Leaf className="h-5 w-5 mr-2" />
                      <span>Your purchase will offset approximately <strong>{(parseInt(quantity) * 1).toLocaleString()} tons</strong> of CO2</span>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="retire"
                      checked={retireCredits}
                      onChange={(e) => setRetireCredits(e.target.checked)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <label htmlFor="retire" className="ml-2 text-sm text-gray-700">
                      Retire credits immediately (recommended for offsetting)
                    </label>
                    <div className="ml-2 group relative">
                      <Info className="h-4 w-4 text-gray-400 cursor-help" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity w-64">
                        Retiring credits means they cannot be resold and are permanently removed from circulation, ensuring no double counting.
                      </div>
                    </div>
                  </div>

                  {retireCredits && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Retirement Reason *
                      </label>
                      <select
                        value={retirementReason}
                        onChange={(e) => setRetirementReason(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                          errors.retirementReason ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select retirement reason</option>
                        <option value="carbon-neutral-operations">Carbon Neutral Operations</option>
                        <option value="product-carbon-neutrality">Product Carbon Neutrality</option>
                        <option value="event-offsetting">Event Offsetting</option>
                        <option value="voluntary-commitment">Voluntary Climate Commitment</option>
                        <option value="compliance">Compliance Requirement</option>
                        <option value="csr-initiative">CSR Initiative</option>
                        <option value="other">Other</option>
                      </select>
                      {errors.retirementReason && <p className="text-red-500 text-xs mt-1">{errors.retirementReason}</p>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Payment Information</h3>

            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h4 className="font-medium text-gray-900 mb-2">Order Summary</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>{quantity} credits × ${project.price}</span>
                  <span>${calculateTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Processing fee (3%)</span>
                  <span>${calculateFees().toFixed(2)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-medium">
                  <span>Total</span>
                  <span>${(calculateTotal() + calculateFees()).toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Method *
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="payment"
                      value="credit-card"
                      checked={paymentMethod === 'credit-card'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Credit Card</span>
                    <CreditCard className="h-4 w-4 ml-2 text-gray-400" />
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="payment"
                      value="bank-transfer"
                      checked={paymentMethod === 'bank-transfer'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Bank Transfer</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="payment"
                      value="crypto"
                      checked={paymentMethod === 'crypto'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Cryptocurrency</span>
                  </label>
                </div>
                {errors.paymentMethod && <p className="text-red-500 text-xs mt-1">{errors.paymentMethod}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={paymentDetails.name}
                    onChange={(e) => setPaymentDetails(prev => ({ ...prev, name: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your full name"
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={paymentDetails.email}
                    onChange={(e) => setPaymentDetails(prev => ({ ...prev, email: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your email"
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company/Organization (Optional)
                </label>
                <input
                  type="text"
                  value={paymentDetails.company}
                  onChange={(e) => setPaymentDetails(prev => ({ ...prev, company: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Enter company name"
                />
              </div>

              {paymentMethod === 'credit-card' && (
                <div className="space-y-4 border-t pt-4">
                  <h4 className="font-medium text-gray-900">Card Details</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Card Number *
                    </label>
                    <input
                      type="text"
                      value={paymentDetails.cardNumber}
                      onChange={(e) => setPaymentDetails(prev => ({ ...prev, cardNumber: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                        errors.cardNumber ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="1234 5678 9012 3456"
                    />
                    {errors.cardNumber && <p className="text-red-500 text-xs mt-1">{errors.cardNumber}</p>}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Expiry Date *
                      </label>
                      <input
                        type="text"
                        value={paymentDetails.expiryDate}
                        onChange={(e) => setPaymentDetails(prev => ({ ...prev, expiryDate: e.target.value }))}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                          errors.expiryDate ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="MM/YY"
                      />
                      {errors.expiryDate && <p className="text-red-500 text-xs mt-1">{errors.expiryDate}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        CVV *
                      </label>
                      <input
                        type="text"
                        value={paymentDetails.cvv}
                        onChange={(e) => setPaymentDetails(prev => ({ ...prev, cvv: e.target.value }))}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                          errors.cvv ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="123"
                      />
                      {errors.cvv && <p className="text-red-500 text-xs mt-1">{errors.cvv}</p>}
                    </div>
                  </div>
                </div>
              )}

              {paymentMethod === 'bank-transfer' && (
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Bank Transfer Instructions</h4>
                  <p className="text-sm text-blue-800">
                    After clicking "Complete Purchase", you'll receive bank transfer details via email. 
                    Credits will be issued within 2-3 business days after payment confirmation.
                  </p>
                </div>
              )}

              {paymentMethod === 'crypto' && (
                <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
                  <h4 className="font-medium text-purple-900 mb-2">Cryptocurrency Payment</h4>
                  <p className="text-sm text-purple-800">
                    We accept Bitcoin, Ethereum, and USDC. You'll be redirected to our crypto payment processor 
                    to complete the transaction.
                  </p>
                </div>
              )}
            </div>

            <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
              <div className="flex items-start">
                <Lock className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Secure Payment</h4>
                  <p className="text-sm text-gray-600">
                    Your payment information is encrypted and secure. We use industry-standard security measures 
                    to protect your data and comply with international privacy regulations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="text-center space-y-6">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Purchase Complete!</h3>
              <p className="text-gray-600">
                Thank you for your carbon credit purchase. Your certificate has been generated.
              </p>
            </div>

            <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
              <h4 className="font-semibold text-green-900 mb-4">Purchase Details</h4>
              <div className="space-y-2 text-sm text-green-800">
                <div className="flex justify-between">
                  <span>Certificate ID:</span>
                  <span className="font-mono">{certificateId}</span>
                </div>
                <div className="flex justify-between">
                  <span>Project:</span>
                  <span>{project.title}</span>
                </div>
                <div className="flex justify-between">
                  <span>Credits Purchased:</span>
                  <span>{quantity}</span>
                </div>
                <div className="flex justify-between">
                  <span>Total Amount:</span>
                  <span>${(calculateTotal() + calculateFees()).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Status:</span>
                  <span>{retireCredits ? 'Retired' : 'Active'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Purchase Date:</span>
                  <span>{new Date().toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={downloadCertificate}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center"
              >
                <Download className="h-5 w-5 mr-2" />
                Download Certificate
              </button>

              <button className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center">
                <Share className="h-5 w-5 mr-2" />
                Share Achievement
              </button>
            </div>

            <div className="text-sm text-gray-600">
              <p>A confirmation email has been sent to {paymentDetails.email}</p>
              <p>You can access your certificates anytime in your dashboard.</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Link to={`/marketplace/project/${id}`} className="mr-4">
            <ArrowLeft className="h-6 w-6 text-gray-600 hover:text-gray-800" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Purchase Carbon Credits</h1>
            <p className="text-gray-600">Complete your carbon offset purchase</p>
          </div>
        </div>

        {/* Progress Steps */}
        {!purchaseComplete && (
          <div className="flex items-center justify-center mb-8">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  currentStep >= step 
                    ? 'bg-green-600 border-green-600 text-white' 
                    : 'border-gray-300 text-gray-500'
                }`}>
                  {currentStep > step ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : (
                    <span className="text-sm font-medium">{step}</span>
                  )}
                </div>
                {step < 3 && (
                  <div className={`w-16 h-0.5 mx-2 ${
                    currentStep > step ? 'bg-green-600' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="bg-white rounded-lg shadow-sm border p-8">
        {renderStepContent()}

        {/* Navigation Buttons */}
        {!purchaseComplete && (
          <div className="flex justify-between mt-8 pt-6 border-t">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`px-6 py-2 border border-gray-300 rounded-lg font-medium ${
                currentStep === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>

            {currentStep < 2 ? (
              <button
                onClick={nextStep}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition duration-200"
              >
                Continue
              </button>
            ) : (
              <button
                onClick={handlePurchase}
                disabled={processing}
                className={`px-6 py-2 font-medium rounded-lg transition duration-200 ${
                  processing
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                {processing ? 'Processing...' : `Complete Purchase - $${(calculateTotal() + calculateFees()).toFixed(2)}`}
              </button>
            )}
          </div>
        )}

        {purchaseComplete && (
          <div className="mt-8 pt-6 border-t text-center">
            <Link
              to="/marketplace"
              className="inline-flex items-center px-6 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 mr-4"
            >
              Browse More Projects
            </Link>
            <Link
              to="/marketplace/my-projects"
              className="inline-flex items-center px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg"
            >
              View My Certificates
            </Link>
          </div>
        )}
      </div>

      {/* Security Notice */}
      {currentStep === 2 && !purchaseComplete && (
        <div className="mt-6 bg-blue-50 border border-blue-200 p-4 rounded-lg">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 mb-1">Important Information</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Carbon credits are immediately issued upon payment confirmation</li>
                <li>• All purchases are final and non-refundable</li>
                <li>• Retired credits cannot be resold or transferred</li>
                <li>• Certificates are valid proof of carbon offset for reporting purposes</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseCredits; 